import { useEffect, useState } from 'react';
import {
  View,
  Text,
  Pressable,
  TextInput,
  ActivityIndicator,
  Platform,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
} from 'react-native';
import { type Href, useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import * as Linking from 'expo-linking';
import * as AppleAuthentication from 'expo-apple-authentication';
import * as Crypto from 'expo-crypto';
import { Ionicons } from '@expo/vector-icons';
import { DEMO_ACCOUNTS, type DemoAccountKey } from '@perdiem/trpc/demo-accounts';
import {
  FINANCIAL_ACKNOWLEDGMENT_TEXT,
} from '@perdiem/trpc/legal';
import {
  DemoProfilePickerModal,
  PasswordRequirementList,
  TurnstileCaptcha,
  WebHead,
} from '../../components';
import {
  canUseNativeGoogleSignIn,
  getNativeGoogleSignInErrorMessage,
  signInWithNativeGoogle,
} from '@/lib/google-auth';
import {
  consumePendingAuthRedirect,
  normalizeAuthRedirect,
  setPendingAuthRedirect,
} from '@/lib/auth-redirect';
import { getPendingMarketingAttribution } from '@/lib/marketing-attribution';
import {
  normalizeTrialPromoCode,
  setPendingTrialPromoCode,
} from '@/lib/pending-promo-code';
import {
  trackAuthAttemptFailed,
  trackAuthAttemptStarted,
  trackAuthAttemptSucceeded,
  trackAuthScreenViewed,
  trackAuthUserCreated,
  trackMarketingCtaClicked,
  trackMarketingPageViewed,
} from '@/lib/product-events';
import {
  reportUiError,
  sendClientObservability,
  serializeClientError,
} from '@/lib/observability';
import {
  clearPendingAuthAttempt,
  setPendingAuthAttempt,
  wasAuthUserCreatedDuringAttempt,
} from '@/lib/auth-attempt';
import { signInDemoAccount, signInLocalDevUser, supabase } from '@/lib/supabase';
import { maybeCompleteOAuthSession, openOAuthSessionAsync } from '@/lib/oauth-session';
import { resolveDevelopmentUrl } from '@/lib/dev-url';
import { getPasswordPolicyError } from '@/lib/password-policy';
import { formatCurrency, getLegalUrl, openExternalUrl } from '@/lib/utils';
import {
  clearPendingLegalSignupAcceptance,
  markPendingLegalSignupAcceptance,
} from '@/lib/legal-client';
import { hasSeenAuthEntry, markAuthEntrySeen } from '@/lib/auth-device-state';

maybeCompleteOAuthSession();

// On web, redirect back to the app's own callback URL so Expo Router handles it.
// On native, redirect to a fixed deep link so the URI is stable and can be
// allow-listed in Supabase Auth settings.
const NATIVE_OAUTH_REDIRECT = 'perdiem://callback';
const WEB_OAUTH_ORIGIN =
  typeof globalThis.location?.origin === 'string'
    ? globalThis.location.origin
    : process.env['EXPO_PUBLIC_SITE_URL'] ?? 'http://localhost:8081';
const WEB_CALLBACK_REDIRECT = `${WEB_OAUTH_ORIGIN}/callback`;
const PASSWORD_RESET_REDIRECT = `${WEB_CALLBACK_REDIRECT}?authFlow=password-reset`;
const PASSWORD_RESET_PENDING_STORAGE_KEY = 'perdiem.password-reset-pending';

const DEV_BYPASS = process.env['EXPO_PUBLIC_DEV_BYPASS'] === 'true';
const TURNSTILE_SITE_KEY = process.env['EXPO_PUBLIC_TURNSTILE_SITE_KEY']?.trim() ?? '';
const TURNSTILE_ENABLE_LOCALHOST =
  process.env['EXPO_PUBLIC_TURNSTILE_ENABLE_LOCALHOST'] === 'true';
const LANDING_ROUTE = '/' as Href;
const LOCALHOST_HOSTS = new Set(['localhost', '127.0.0.1']);
const LOCAL_API_URL = (process.env['EXPO_PUBLIC_API_URL'] ?? 'http://localhost:3001').replace(/\/$/, '');
const TERMS_OF_SERVICE_URL = getLegalUrl('terms');
const PRIVACY_POLICY_URL = getLegalUrl('privacy');
const SHOW_NATIVE_DEMO_CTA = Platform.OS !== 'web';
const NATIVE_DEMO_ACCOUNTS = DEMO_ACCOUNTS.filter((account) => account.key !== 'gregfire');
const AUTH_INTRO_DISPLAY_FONT = 'InstrumentSerif_400Regular';
const AUTH_INTRO_MONO_FONT = 'DMMono_400Regular';
const AUTH_INTRO_MONO_FONT_MEDIUM = 'DMMono_500Medium';

type NativeAuthEntryStatus = 'loading' | 'fresh' | 'returning';

type LocalDevUserTile = {
  supabaseUserId: string;
  email: string | null;
  isSupportClone: boolean;
  sourceHouseholdId: string | null;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  personId: string | null;
  personType: string | null;
  householdId: string | null;
  onboardingStep: number | null;
  householdCreatedAt: string | null;
  accountStatus: string | null;
  entitlement: string | null;
  entitlementExpiresAt: string | null;
  subscriptionStore: string | null;
  subscriptionStatus: string | null;
  subscriptionAutoRenewalStatus: string | null;
  subscriptionCurrentPeriodEndsAt: string | null;
  subscriptionLifecycleState: string | null;
  trialExpiresAt: string | null;
  fiAge: number | null;
  netWorth: number | null;
  createdAt: string;
};

type SupportCloneBulkJob = {
  id: string;
  status: 'running' | 'completed' | 'failed';
  eligibleHouseholds: number | null;
  clonedCount: number;
  failuresCount: number;
  currentHouseholdId: string | null;
  currentEmail: string | null;
  error: string | null;
  failures: Array<{ householdId: string; email: string | null; error: string }>;
};

function NativeIntroLeafMark() {
  return (
    <View style={authIntroStyles.leafMark}>
      <Ionicons name="leaf-outline" size={20} color="#ffffff" />
    </View>
  );
}

const authIntroStyles = StyleSheet.create({
  root: {
    flex: 1,
  },
  glowLayer: {
    ...StyleSheet.absoluteFill,
  },
  blueGlow: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 470,
  },
  greenGlow: {
    position: 'absolute',
    top: 260,
    left: 0,
    right: 0,
    height: 520,
  },
  safeArea: {
    flex: 1,
  },
  nav: {
    paddingHorizontal: 24,
    paddingTop: 18,
    paddingBottom: 20,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: 'rgba(242,244,239,0.13)',
    backgroundColor: 'rgba(10,14,11,0.78)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
  },
  navCompact: {
    paddingTop: 12,
    paddingBottom: 14,
  },
  brandLockup: {
    minWidth: 0,
    flexShrink: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  leafMark: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#3CB778',
  },
  brandName: {
    color: '#F2F4EF',
    fontSize: 17,
    fontWeight: '700',
    letterSpacing: 0,
    lineHeight: 20,
  },
  brandTag: {
    color: 'rgba(242,244,239,0.46)',
    fontSize: 13,
    lineHeight: 16,
  },
  navActions: {
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  navLoginButton: {
    width: 64,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  navLoginText: {
    color: 'rgba(242,244,239,0.68)',
    fontFamily: AUTH_INTRO_MONO_FONT,
    fontSize: 11,
    letterSpacing: 1.2,
    lineHeight: 16,
    textTransform: 'uppercase',
  },
  navStartPressable: {
    borderRadius: 999,
  },
  navStartPill: {
    width: 110,
    minHeight: 48,
    borderRadius: 999,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#F2F4EF',
  },
  navStartText: {
    color: '#06180D',
    fontFamily: AUTH_INTRO_MONO_FONT_MEDIUM,
    fontSize: 12,
    letterSpacing: 1.25,
    lineHeight: 16,
    textTransform: 'uppercase',
  },
  hero: {
    flex: 1,
    paddingHorizontal: 8,
    paddingTop: 86,
    paddingBottom: 34,
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 24,
  },
  heroCompact: {
    paddingTop: 62,
    paddingBottom: 22,
    gap: 16,
  },
  eyebrow: {
    minHeight: 32,
    maxWidth: '100%',
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(60,183,120,0.42)',
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    backgroundColor: 'rgba(60,183,120,0.08)',
  },
  eyebrowDot: {
    width: 7,
    height: 7,
    borderRadius: 999,
    backgroundColor: '#3CB778',
  },
  eyebrowText: {
    color: '#3CB778',
    fontFamily: AUTH_INTRO_MONO_FONT,
    fontSize: 11,
    letterSpacing: 2.2,
    lineHeight: 14,
    textTransform: 'uppercase',
  },
  heroTitle: {
    width: '100%',
    maxWidth: 382,
    color: '#F2F4EF',
    fontFamily: AUTH_INTRO_DISPLAY_FONT,
    fontSize: 48,
    fontWeight: '400',
    letterSpacing: 0,
    lineHeight: 50,
    textAlign: 'center',
  },
  heroTitleCompact: {
    fontSize: 42,
    lineHeight: 44,
  },
  heroSub: {
    maxWidth: '100%',
    color: 'rgba(242,244,239,0.68)',
    fontSize: 20,
    lineHeight: 32,
    textAlign: 'center',
  },
  heroSubCompact: {
    fontSize: 18,
    lineHeight: 29,
  },
  ctas: {
    width: '100%',
    alignItems: 'center',
    gap: 14,
  },
  ctasCompact: {
    gap: 10,
  },
  primaryButtonPressable: {
    width: '100%',
    maxWidth: 310,
    borderRadius: 999,
  },
  primaryButtonPill: {
    minHeight: 48,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#F2F4EF',
    paddingHorizontal: 24,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    backgroundColor: '#F2F4EF',
  },
  primaryButtonText: {
    color: '#06180D',
    fontFamily: AUTH_INTRO_MONO_FONT_MEDIUM,
    fontSize: 12,
    letterSpacing: 1.25,
    lineHeight: 17,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  secondaryButtonPressable: {
    width: '100%',
    maxWidth: 270,
    borderRadius: 999,
  },
  secondaryButtonPill: {
    minHeight: 46,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(242,244,239,0.22)',
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(242,244,239,0.07)',
  },
  secondaryButtonText: {
    color: '#F2F4EF',
    fontFamily: AUTH_INTRO_MONO_FONT_MEDIUM,
    fontSize: 12,
    letterSpacing: 1.5,
    lineHeight: 17,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  buttonPressed: {
    transform: [{ translateY: 1 }],
    opacity: 0.92,
  },
  meta: {
    maxWidth: 340,
    marginTop: 8,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  metaCompact: {
    marginTop: 2,
    gap: 10,
  },
  metaText: {
    color: 'rgba(242,244,239,0.46)',
    fontFamily: AUTH_INTRO_MONO_FONT,
    fontSize: 10,
    letterSpacing: 2.1,
    lineHeight: 16,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
});

const authFormStyles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
    paddingVertical: 48,
  },
  form: {
    width: '100%',
  },
});

function NativeFreshInstallIntro({
  onStart,
  onSignIn,
  onTryDemo,
}: {
  onStart: () => void;
  onSignIn: () => void;
  onTryDemo: () => void;
}) {
  const { height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const isCompact = height < 760;
  const navVerticalPadding = isCompact ? 12 : 18;

  return (
    <LinearGradient
      colors={['#101A1F', '#0A0E0B', '#061009']}
      locations={[0, 0.55, 1]}
      style={authIntroStyles.root}
    >
      <View pointerEvents="none" style={authIntroStyles.glowLayer}>
        <LinearGradient
          colors={[
            'rgba(94,143,184,0.24)',
            'rgba(94,143,184,0.09)',
            'rgba(10,14,11,0)',
          ]}
          locations={[0, 0.42, 1]}
          style={authIntroStyles.blueGlow}
        />
        <LinearGradient
          colors={[
            'rgba(60,183,120,0)',
            'rgba(60,183,120,0.075)',
            'rgba(60,183,120,0)',
          ]}
          locations={[0, 0.45, 1]}
          style={authIntroStyles.greenGlow}
        />
      </View>
      <SafeAreaView style={authIntroStyles.safeArea} edges={['bottom']}>
        <WebHead><title>Start – Per Diem</title></WebHead>

        <View
          style={[
            authIntroStyles.nav,
            isCompact ? authIntroStyles.navCompact : null,
            {
              minHeight: insets.top + (isCompact ? 82 : 96),
              paddingTop: insets.top + navVerticalPadding,
            },
          ]}
        >
          <View style={authIntroStyles.brandLockup}>
            <NativeIntroLeafMark />
            <View>
              <Text style={authIntroStyles.brandName}>Per Diem</Text>
              <Text style={authIntroStyles.brandTag}>Every day matters</Text>
            </View>
          </View>

          <View style={authIntroStyles.navActions}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Sign in"
              onPress={onSignIn}
              style={authIntroStyles.navLoginButton}
            >
              <Text numberOfLines={1} style={authIntroStyles.navLoginText}>Log in</Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Create account"
              onPress={onStart}
              style={({ pressed }) => [
                authIntroStyles.navStartPressable,
                pressed ? authIntroStyles.buttonPressed : null,
              ]}
            >
              <View style={authIntroStyles.navStartPill}>
                <Text numberOfLines={1} style={authIntroStyles.navStartText}>Start</Text>
                <Ionicons name="arrow-forward" size={16} color="#06180D" />
              </View>
            </Pressable>
          </View>
        </View>

        <View style={[authIntroStyles.hero, isCompact ? authIntroStyles.heroCompact : null]}>
          <View style={authIntroStyles.eyebrow}>
            <View style={authIntroStyles.eyebrowDot} />
            <Text style={authIntroStyles.eyebrowText}>iOS, Android, Web, ChatGPT</Text>
          </View>

          <Text
            accessibilityRole="header"
            adjustsFontSizeToFit
            minimumFontScale={0.86}
            numberOfLines={3}
            style={[authIntroStyles.heroTitle, isCompact ? authIntroStyles.heroTitleCompact : null]}
          >
            Your spending.{'\n'}Your portfolio.{'\n'}Your FIRE plan.
          </Text>

          <Text style={[authIntroStyles.heroSub, isCompact ? authIntroStyles.heroSubCompact : null]}>
            Per Diem connects your accounts, portfolio,{'\n'}
            and path to financial independence into one{'\n'}
            daily number: what you can spend today{'\n'}
            while keeping the long-term plan on track.
          </Text>

          <View style={[authIntroStyles.ctas, isCompact ? authIntroStyles.ctasCompact : null]}>
            <Pressable
              testID="auth-intro-signup-button"
              accessibilityRole="button"
              accessibilityLabel="Start 14 days free"
              onPress={onStart}
              style={({ pressed }) => [
                authIntroStyles.primaryButtonPressable,
                pressed ? authIntroStyles.buttonPressed : null,
              ]}
            >
              <View style={authIntroStyles.primaryButtonPill}>
                <Text numberOfLines={1} style={authIntroStyles.primaryButtonText}>
                  Start 14 days free
                </Text>
                <Ionicons name="arrow-forward" size={16} color="#06180D" />
              </View>
            </Pressable>

            {SHOW_NATIVE_DEMO_CTA ? (
              <Pressable
                testID="auth-intro-demo-button"
                accessibilityRole="button"
                accessibilityLabel="Try a demo profile"
                onPress={onTryDemo}
                style={({ pressed }) => [
                  authIntroStyles.secondaryButtonPressable,
                  pressed ? authIntroStyles.buttonPressed : null,
                ]}
              >
                <View style={authIntroStyles.secondaryButtonPill}>
                  <Text numberOfLines={1} style={authIntroStyles.secondaryButtonText}>
                    Try a demo profile
                  </Text>
                </View>
              </Pressable>
            ) : null}
          </View>

          <View style={[authIntroStyles.meta, isCompact ? authIntroStyles.metaCompact : null]}>
            <Text style={authIntroStyles.metaText}>Built for the FIRE community</Text>
            <Text style={authIntroStyles.metaText}>Chat with your finances</Text>
            <Text style={authIntroStyles.metaText}>14-day free trial</Text>
          </View>
        </View>
      </SafeAreaView>
    </LinearGradient>
  );
}

function isLocalhostWeb() {
  return (
    Platform.OS === 'web' &&
    LOCALHOST_HOSTS.has(globalThis.location?.hostname ?? '')
  );
}

function getLocalUsersEndpoint() {
  return `${resolveDevelopmentUrl(LOCAL_API_URL)}/dev/local-users`;
}

function getSupportClonesEndpoint() {
  return `${resolveDevelopmentUrl(LOCAL_API_URL)}/dev/support-clones`;
}

function getFullyOnboardedSupportClonesEndpoint() {
  return `${getSupportClonesEndpoint()}/fully-onboarded`;
}

function getFullyOnboardedSupportCloneJobEndpoint(jobId: string) {
  return `${getFullyOnboardedSupportClonesEndpoint()}/${encodeURIComponent(jobId)}`;
}

function getLocalUserEndpoint(supabaseUserId: string) {
  return `${getLocalUsersEndpoint()}/${encodeURIComponent(supabaseUserId)}`;
}

function wait(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

function getTimestampMs(value: string | null) {
  if (!value) return 0;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function formatLocalDevDate(value: string | null) {
  if (!value) return 'Unknown';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function formatLocalDevShortDate(value: string | null) {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function normalizeLocalDevStatus(value: string | null) {
  return value?.trim().toLowerCase() ?? null;
}

function isLocalDevDateInFuture(value: string | null) {
  const timestamp = getTimestampMs(value);
  return timestamp > Date.now();
}

function getLocalDevOnboardingLabel(user: LocalDevUserTile) {
  if (!user.householdId) return 'No household yet';
  return user.onboardingStep != null && user.onboardingStep >= 9
    ? 'Fully onboarded'
    : `Onboarding step ${user.onboardingStep ?? 0}`;
}

function formatLocalDevEnrollmentStatus(user: LocalDevUserTile) {
  if (!user.householdId) return 'Auth user only';

  const accountStatus = normalizeLocalDevStatus(user.accountStatus);
  const lifecycleState = normalizeLocalDevStatus(user.subscriptionLifecycleState);
  const subscriptionStatus = normalizeLocalDevStatus(user.subscriptionStatus);
  const autoRenewalStatus = normalizeLocalDevStatus(user.subscriptionAutoRenewalStatus);
  const trialDate = formatLocalDevShortDate(user.trialExpiresAt);
  const entitlementDate = formatLocalDevShortDate(user.entitlementExpiresAt);
  const subscriptionPeriodDate = formatLocalDevShortDate(user.subscriptionCurrentPeriodEndsAt);
  const trialIsActive = trialDate && isLocalDevDateInFuture(user.trialExpiresAt);
  const subscriptionPeriodIsActive =
    !user.subscriptionCurrentPeriodEndsAt ||
    isLocalDevDateInFuture(user.subscriptionCurrentPeriodEndsAt);
  const subscriptionLooksActive =
    lifecycleState === 'subscription_active' ||
    lifecycleState === 'subscription_canceled' ||
    subscriptionStatus === 'active' ||
    Boolean(subscriptionStatus && subscriptionStatus !== 'expired' && subscriptionPeriodIsActive);
  const subscriptionWillNotRenew =
    lifecycleState === 'subscription_canceled' ||
    autoRenewalStatus === 'will_not_renew';
  const subscriptionWillRenew =
    lifecycleState === 'subscription_active' ||
    autoRenewalStatus === 'will_renew';
  const accountIsInactive = Boolean(accountStatus && accountStatus !== 'active');
  const trialHasLapsed =
    lifecycleState === 'trial_expired_grace' ||
    Boolean(user.trialExpiresAt && !isLocalDevDateInFuture(user.trialExpiresAt));
  const subscriptionHasLapsed =
    lifecycleState === 'subscription_expired' ||
    subscriptionStatus === 'expired' ||
    Boolean(user.subscriptionCurrentPeriodEndsAt && !subscriptionPeriodIsActive);
  const inactivePrefix =
    accountIsInactive || subscriptionHasLapsed || trialHasLapsed ? 'inactive' : null;

  if (accountIsInactive) {
    if (trialHasLapsed) {
      return 'inactive: trial lapsed';
    }
    if (subscriptionPeriodDate) {
      return `inactive: sub cancelled on ${subscriptionPeriodDate}`;
    }
    if (subscriptionHasLapsed) {
      return 'inactive: sub cancelled';
    }
    return `inactive: ${accountStatus}`;
  }

  if (trialIsActive && (lifecycleState === 'trial_active' || lifecycleState === 'trial_expiring')) {
    return `free trial expires on ${trialDate}`;
  }

  if (subscriptionLooksActive && subscriptionPeriodDate) {
    if (subscriptionWillNotRenew) {
      return `sub ends on ${subscriptionPeriodDate}`;
    }
    if (subscriptionWillRenew || subscriptionStatus === 'active') {
      return `sub renews on ${subscriptionPeriodDate}`;
    }
    return `sub active until ${subscriptionPeriodDate}`;
  }

  if (subscriptionLooksActive) {
    return subscriptionWillNotRenew ? 'sub cancelled' : 'sub active';
  }

  if (inactivePrefix && trialHasLapsed) {
    return `${inactivePrefix}: trial lapsed`;
  }

  if (inactivePrefix && subscriptionHasLapsed) {
    return subscriptionPeriodDate
      ? `${inactivePrefix}: sub cancelled on ${subscriptionPeriodDate}`
      : `${inactivePrefix}: sub cancelled`;
  }

  if (trialIsActive) {
    return `free trial expires on ${trialDate}`;
  }

  if (user.entitlement && entitlementDate) {
    return isLocalDevDateInFuture(user.entitlementExpiresAt)
      ? `entitlement expires on ${entitlementDate}`
      : `inactive: entitlement expired on ${entitlementDate}`;
  }

  if (user.entitlement) {
    return 'manual entitlement active';
  }

  if (accountStatus && accountStatus !== 'active') {
    return `inactive: ${accountStatus}`;
  }

  return accountStatus === 'active' ? 'active: no enrollment details' : 'No enrollment details';
}

function formatSupportCloneBulkJobMessage(job: SupportCloneBulkJob) {
  const total = job.eligibleHouseholds;
  const totalLabel =
    total === null
      ? 'fully onboarded households'
      : `${total} fully onboarded household${total === 1 ? '' : 's'}`;
  const failureSuffix =
    job.failuresCount > 0
      ? ` ${job.failuresCount} failed${job.failures[0]?.error ? `; first: ${job.failures[0].error}` : '.'}`
      : '';

  if (job.status === 'running') {
    if (total === null) {
      return 'Finding eligible fully onboarded households...';
    }

    const currentTarget = job.currentEmail ?? job.currentHouseholdId;
    const current = currentTarget ? ` Current: ${currentTarget}.` : '';
    return `Cloned ${job.clonedCount} of ${totalLabel}.${current}${failureSuffix}`;
  }

  if (job.status === 'failed') {
    return `Clone all failed after ${job.clonedCount} cloned household${job.clonedCount === 1 ? '' : 's'}: ${job.error ?? 'unknown error'}`;
  }

  return `Cloned ${job.clonedCount} of ${totalLabel}.${failureSuffix}`;
}

function markPasswordResetPending(email: string) {
  if (Platform.OS !== 'web' || typeof localStorage === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(
      PASSWORD_RESET_PENDING_STORAGE_KEY,
      JSON.stringify({
        email,
        createdAt: new Date().toISOString(),
      }),
    );
  } catch {
    // Non-critical: the redirect URL also carries the recovery intent.
  }
}

function clearPasswordResetPending() {
  if (Platform.OS !== 'web' || typeof localStorage === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(PASSWORD_RESET_PENDING_STORAGE_KEY);
  } catch {
    // Ignore storage cleanup failures.
  }
}

async function fetchLocalDevUsers() {
  const response = await fetch(getLocalUsersEndpoint());
  const payload = (await response.json()) as { users?: LocalDevUserTile[]; error?: string };

  if (!response.ok) {
    throw new Error(payload.error ?? 'Failed to load local users.');
  }

  return Array.isArray(payload.users) ? payload.users : [];
}

function createNonce(length = 32) {
  const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  let nonce = '';

  for (let index = 0; index < length; index += 1) {
    nonce += chars[Math.floor(Math.random() * chars.length)];
  }

  return nonce;
}

function getFullName(fullName: AppleAuthentication.AppleAuthenticationFullName | null) {
  if (!fullName) return null;

  const parts = [fullName.givenName, fullName.middleName, fullName.familyName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
}

function getAuthErrorBucket(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim().slice(0, 80);
  }

  if (typeof error === 'string' && error.trim()) {
    return error.trim().slice(0, 80);
  }

  return 'unknown';
}

function isExpectedEmailSignupProviderError(error: unknown): boolean {
  const message = getAuthErrorBucket(error).toLowerCase();
  return [
    'already registered',
    'email rate limit',
    'for security purposes',
    'invalid email',
    'password cannot',
    'password must',
    'password should',
    'rate limit',
    'too many requests',
    'weak password',
  ].some((needle) => message.includes(needle));
}

function getOAuthCallbackRedirect(requestedRedirect: string | null): string {
  if (Platform.OS !== 'web') return NATIVE_OAUTH_REDIRECT;
  if (!requestedRedirect) return WEB_CALLBACK_REDIRECT;

  const callbackUrl = new URL(WEB_CALLBACK_REDIRECT);
  callbackUrl.searchParams.set('redirect', requestedRedirect);
  return callbackUrl.toString();
}

async function saveAuthProfileMetadata(
  profile: Partial<{
    email: string | null;
    full_name: string | null;
    given_name: string | null;
    family_name: string | null;
    avatar_url: string | null;
  }>,
) {
  const metadata = Object.fromEntries(
    Object.entries(profile).filter(([, value]) => value != null && value !== ''),
  );

  if (Object.keys(metadata).length === 0) {
    return;
  }

  const { error } = await supabase.auth.updateUser({ data: metadata });

  if (error) {
    console.warn('[auth] Failed to save auth profile metadata:', error.message);
  }
}

async function signInWithProviderIdToken(params: {
  provider: 'apple' | 'google';
  token: string;
  accessToken?: string;
  captchaToken?: string | null;
  nonce?: string;
}) {
  const { data, error } = await supabase.auth.signInWithIdToken({
    provider: params.provider,
    token: params.token,
    ...(params.accessToken ? { access_token: params.accessToken } : {}),
    ...(params.captchaToken ? { options: { captchaToken: params.captchaToken } } : {}),
    ...(params.nonce ? { nonce: params.nonce } : {}),
  });

  if (error) {
    throw error;
  }

  return data;
}

export default function LoginScreen() {
  const router = useRouter();
  const pathname = usePathname();
  const {
    redirect,
    regular,
    mode: requestedModeParam,
    promo,
    disablePasswordAutofill,
    disableSecurityCheck,
  } = useLocalSearchParams<{
    redirect?: string;
    regular?: string;
    mode?: string;
    promo?: string;
    disablePasswordAutofill?: string;
    disableSecurityCheck?: string;
  }>();
  const requestedMode =
    requestedModeParam === 'signup'
      ? 'signup'
      : requestedModeParam === 'signin'
        ? 'signin'
        : null;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedLegalTerms, setAcceptedLegalTerms] = useState(false);
  const [acceptedFinancialAck, setAcceptedFinancialAck] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<'signin' | 'signup'>(requestedMode ?? 'signin');
  const [nativeAuthIntroRequested, setNativeAuthIntroRequested] = useState(false);
  const [nativeAuthEntryStatus, setNativeAuthEntryStatus] =
    useState<NativeAuthEntryStatus>(() =>
      Platform.OS === 'web' || requestedMode ? 'returning' : 'loading',
    );
  const [isResetMode, setIsResetMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isAppleAvailable, setIsAppleAvailable] = useState(false);
  const [localUsers, setLocalUsers] = useState<LocalDevUserTile[]>([]);
  const [isLoadingLocalUsers, setIsLoadingLocalUsers] = useState(false);
  const [localUsersError, setLocalUsersError] = useState<string | null>(null);
  const [impersonatingUserId, setImpersonatingUserId] = useState<string | null>(null);
  const [showDemoPicker, setShowDemoPicker] = useState(false);
  const [launchingDemoKey, setLaunchingDemoKey] = useState<DemoAccountKey | null>(null);
  const [activeLocalUserGroup, setActiveLocalUserGroup] = useState<'test' | 'support'>('test');
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [cloneSelectorType, setCloneSelectorType] = useState<'email' | 'supabaseUserId' | 'householdId'>('email');
  const [cloneSelectorValue, setCloneSelectorValue] = useState('');
  const [cloneLocalEmail, setCloneLocalEmail] = useState('');
  const [isCloningHousehold, setIsCloningHousehold] = useState(false);
  const [isCloningFullyOnboarded, setIsCloningFullyOnboarded] = useState(false);
  const [isDeletingAllSupportClones, setIsDeletingAllSupportClones] = useState(false);
  const [cloneMessage, setCloneMessage] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const isRegularLoginRequested = regular === '1';
  const isLocalhost = isLocalhostWeb();
  const showLocalUserPicker = isLocalhost && !isRegularLoginRequested;
  const shouldDisableSecurityCheck = __DEV__ && disableSecurityCheck === '1';
  const isCaptchaEnabled =
    TURNSTILE_SITE_KEY.length > 0 &&
    (!isLocalhost || TURNSTILE_ENABLE_LOCALHOST) &&
    !shouldDisableSecurityCheck;
  const shouldDisablePasswordAutofill = __DEV__ && disablePasswordAutofill === '1';
  const requestedPromoCode = normalizeTrialPromoCode(typeof promo === 'string' ? promo : null);
  const requestedAuthRedirect = normalizeAuthRedirect(
    typeof redirect === 'string' ? redirect : null,
  );
  const canShowStoredFreshInstallIntro =
    SHOW_NATIVE_DEMO_CTA &&
    !requestedMode &&
    !requestedAuthRedirect &&
    !isRegularLoginRequested;
  const canShowFreshInstallIntro =
    nativeAuthIntroRequested || canShowStoredFreshInstallIntro;
  const showFreshInstallIntro =
    canShowFreshInstallIntro && nativeAuthEntryStatus === 'fresh' && !isResetMode;
  const isCheckingFreshInstallIntro =
    canShowFreshInstallIntro && nativeAuthEntryStatus === 'loading';

  const resetCaptcha = () => {
    if (!isCaptchaEnabled) {
      return;
    }

    setCaptchaToken(null);
    setCaptchaError(null);
    setCaptchaResetKey((current) => current + 1);
  };

  const getCaptchaTokenForAuth = (
    provider: 'email' | 'google' | 'apple',
    flow: 'password' | 'oauth_web' | 'oauth_native' | 'id_token_native',
  ) => {
    if (!isCaptchaEnabled) {
      return null;
    }

    if (captchaToken) {
      setCaptchaError(null);
      return captchaToken;
    }

    const message = 'Please complete the security check.';
    trackAuthAttemptFailed({
      route: pathname,
      intent: mode,
      provider,
      flow,
      failureStage: 'validation',
      errorBucket: 'captcha_missing',
    });
    setCaptchaError(message);
    setError(message);
    return undefined;
  };

  useEffect(() => {
    if (Platform.OS !== 'ios') return;

    AppleAuthentication.isAvailableAsync()
      .then(setIsAppleAvailable)
      .catch(() => setIsAppleAvailable(false));
  }, []);

  useEffect(() => {
    if (!requestedMode) return;
    setMode(requestedMode);
    void markAuthEntrySeen();
  }, [requestedMode]);

  useEffect(() => {
    if (nativeAuthIntroRequested) {
      return;
    }

    if (!canShowStoredFreshInstallIntro) {
      setNativeAuthEntryStatus('returning');
      return;
    }

    let cancelled = false;

    setNativeAuthEntryStatus('loading');
    void hasSeenAuthEntry()
      .then((hasSeen) => {
        if (!cancelled) {
          setNativeAuthEntryStatus(hasSeen ? 'returning' : 'fresh');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setNativeAuthEntryStatus('returning');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canShowStoredFreshInstallIntro, nativeAuthIntroRequested]);

  useEffect(() => {
    if (!requestedPromoCode) return;
    void setPendingTrialPromoCode(requestedPromoCode);
  }, [requestedPromoCode]);

  useEffect(() => {
    if (!requestedAuthRedirect) return;

    void setPendingAuthRedirect(requestedAuthRedirect);
  }, [requestedAuthRedirect]);

  useEffect(() => {
    void (async () => {
      const attribution = await getPendingMarketingAttribution();
      trackMarketingPageViewed({
        route: pathname,
        pageType: 'auth',
        pageSlug: 'login',
        title: 'Per Diem sign in',
        metadata: {
          marketingSource: attribution?.source ?? null,
          marketingPageType: attribution?.pageType ?? null,
          marketingPageSlug: attribution?.pageSlug ?? null,
          marketingArticleSlug: attribution?.articleSlug ?? null,
          marketingCtaId: attribution?.ctaId ?? null,
        },
      });
    })();
  }, [pathname]);

  useEffect(() => {
    trackAuthScreenViewed({
      route: pathname,
      mode,
    });
  }, [mode, pathname]);

  useEffect(() => {
    if (!showLocalUserPicker) {
      return;
    }

    let cancelled = false;

    setIsLoadingLocalUsers(true);
    setLocalUsersError(null);

    void fetchLocalDevUsers()
      .then((users) => {
        if (!cancelled) {
          setLocalUsers(users);
        }
      })
      .catch((fetchError: unknown) => {
        if (!cancelled) {
          setLocalUsers([]);
          setLocalUsersError(
            fetchError instanceof Error ? fetchError.message : 'Failed to load local users.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingLocalUsers(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [showLocalUserPicker]);

  const buildLoginRoute = (nextRegular: boolean) => {
    const params = new URLSearchParams();
    if (nextRegular) {
      params.set('regular', '1');
    }
    if (typeof redirect === 'string' && redirect.length > 0) {
      params.set('redirect', redirect);
    }
    if (requestedMode) {
      params.set('mode', requestedMode);
    }

    const query = params.toString();
    return (`/(auth)/login${query ? `?${query}` : ''}`) as Href;
  };

  const persistRequestedAuthRedirect = async () => {
    if (!requestedAuthRedirect) return;
    await setPendingAuthRedirect(requestedAuthRedirect);
  };

  const redirectAfterSuccessfulAuth = async () => {
    const pendingRedirect = await consumePendingAuthRedirect();
    await markAuthEntrySeen();
    router.replace((requestedAuthRedirect ?? pendingRedirect ?? '/(app)') as any);
  };
  const oauthCallbackRedirect = getOAuthCallbackRedirect(requestedAuthRedirect);

  const handleDevLogin = async () => {
    await redirectAfterSuccessfulAuth();
  };

  const handleAuthBrandPress = () => {
    if (Platform.OS === 'web') {
      router.push(LANDING_ROUTE);
      return;
    }

    trackMarketingCtaClicked({
      route: pathname,
      pageType: 'auth',
      pageSlug: 'login',
      ctaId: 'native-auth-brand-intro',
      destination: 'native_auth_intro',
      placement: 'auth-brand',
    });
    setNativeAuthIntroRequested(true);
    setNativeAuthEntryStatus('fresh');
    setMode('signin');
    setIsResetMode(false);
    setError(null);
    setSuccess(null);
    setPassword('');
    setConfirmPassword('');
    setAcceptedLegalTerms(false);
    setAcceptedFinancialAck(false);
    setCaptchaError(null);
    setCaptchaToken(null);
  };

  const handleOpenDemoPicker = () => {
    void markAuthEntrySeen();
    trackMarketingCtaClicked({
      route: pathname,
      pageType: 'auth',
      pageSlug: 'login',
      ctaId: 'view-demo-profiles',
      destination: 'demo_profiles',
      placement: 'mobile-login',
    });
    setShowDemoPicker(true);
  };

  const handleFreshInstallAuthChoice = (nextMode: 'signin' | 'signup') => {
    trackMarketingCtaClicked({
      route: pathname,
      pageType: 'auth',
      pageSlug: 'login',
      ctaId: nextMode === 'signup' ? 'fresh-install-create-account' : 'fresh-install-sign-in',
      destination: nextMode === 'signup' ? 'create-account' : 'sign-in',
      placement: 'native-auth-intro',
    });
    setNativeAuthEntryStatus('returning');
    setNativeAuthIntroRequested(false);
    setMode(nextMode);
    setError(null);
    setSuccess(null);
    setPassword('');
    setConfirmPassword('');
    setAcceptedLegalTerms(false);
    setAcceptedFinancialAck(false);
    setCaptchaError(null);
    setCaptchaToken(null);
    void markAuthEntrySeen();
  };

  const handleLaunchDemo = async (accountKey: DemoAccountKey) => {
    setLaunchingDemoKey(accountKey);
    setError(null);
    setSuccess(null);

    try {
      await markAuthEntrySeen();
      await signInDemoAccount(accountKey);
      setShowDemoPicker(false);
      router.replace('/(app)' as Href);
    } catch (demoError) {
      reportUiError({
        error: demoError,
        message: 'Demo login failed',
        route: pathname,
        screen: 'auth',
        component: 'LoginScreen',
        metadata: {
          demoAccountKey: accountKey,
          platform: Platform.OS,
        },
      });
      setError('Could not open that demo profile. Please try again.');
    } finally {
      setLaunchingDemoKey(null);
    }
  };

  const handleLocalUserLogin = async (user: LocalDevUserTile) => {
    setImpersonatingUserId(user.supabaseUserId);
    setLocalUsersError(null);

    try {
      await signInLocalDevUser({
        supabaseUserId: user.supabaseUserId,
        email: user.email,
        displayName: user.displayName,
      });
      await redirectAfterSuccessfulAuth();
    } catch (loginError) {
      setLocalUsersError(
        loginError instanceof Error ? loginError.message : 'Failed to sign in as local user.',
      );
    } finally {
      setImpersonatingUserId(null);
    }
  };

  const refreshLocalUsers = async () => {
    const users = await fetchLocalDevUsers();
    setLocalUsers(users);
    return users;
  };

  const handleDeleteLocalUser = async (user: LocalDevUserTile) => {
    if (Platform.OS === 'web') {
      const confirmed = globalThis.confirm(
        `Delete ${user.displayName}'s local household and auth user? This cannot be undone.`,
      );
      if (!confirmed) return;
    }

    setDeletingUserId(user.supabaseUserId);
    setLocalUsersError(null);
    setCloneMessage(null);

    try {
      const response = await fetch(getLocalUserEndpoint(user.supabaseUserId), {
        method: 'DELETE',
      });
      const payload = (await response.json()) as {
        deleted?: { householdId: string | null; authUsers: number };
        error?: string;
      };

      if (!response.ok) {
        throw new Error(payload.error ?? 'Failed to delete local user.');
      }

      await refreshLocalUsers();
      setCloneMessage(
        payload.deleted?.householdId
          ? `Deleted household ${payload.deleted.householdId} and ${payload.deleted.authUsers} auth user${payload.deleted.authUsers === 1 ? '' : 's'}.`
          : `Deleted ${payload.deleted?.authUsers ?? 1} auth user${payload.deleted?.authUsers === 1 ? '' : 's'}.`,
      );
    } catch (deleteError) {
      setLocalUsersError(
        deleteError instanceof Error ? deleteError.message : 'Failed to delete local user.',
      );
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleCloneHousehold = async () => {
    setIsCloningHousehold(true);
    setLocalUsersError(null);
    setCloneMessage(null);

    try {
      const response = await fetch(getSupportClonesEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selectorType: cloneSelectorType,
          selectorValue: cloneSelectorValue,
          localEmail: cloneLocalEmail,
        }),
      });
      const payload = (await response.json()) as {
        clone?: {
          sourceHouseholdId: string;
          localHouseholdId: string;
          localAuthUsers: Array<{ email: string; supabaseUserId: string }>;
        };
        error?: string;
      };

      if (!response.ok) {
        throw new Error(payload.error ?? 'Failed to clone household.');
      }

      await refreshLocalUsers();
      setActiveLocalUserGroup('support');
      setCloneSelectorValue('');
      setCloneLocalEmail('');
      setCloneMessage(
        payload.clone
          ? `Cloned ${payload.clone.sourceHouseholdId} into ${payload.clone.localHouseholdId}.`
          : 'Support household cloned.',
      );
    } catch (cloneError) {
      setLocalUsersError(
        cloneError instanceof Error ? cloneError.message : 'Failed to clone household.',
      );
    } finally {
      setIsCloningHousehold(false);
    }
  };

  const handleDeleteAllSupportClones = async () => {
    if (Platform.OS === 'web') {
      const confirmed = globalThis.confirm(
        'Delete every local cloned support household and auth user? This cannot be undone.',
      );
      if (!confirmed) return;
    }

    setIsDeletingAllSupportClones(true);
    setLocalUsersError(null);
    setCloneMessage(null);

    try {
      const response = await fetch(getSupportClonesEndpoint(), {
        method: 'DELETE',
      });
      const payload = (await response.json()) as {
        deleted?: { households: number; authUsers: number };
        error?: string;
      };

      if (!response.ok) {
        throw new Error(payload.error ?? 'Failed to delete cloned support users.');
      }

      await refreshLocalUsers();
      const households = payload.deleted?.households ?? 0;
      const authUsers = payload.deleted?.authUsers ?? 0;
      setCloneMessage(
        `Deleted ${households} cloned household${households === 1 ? '' : 's'} and ${authUsers} auth user${authUsers === 1 ? '' : 's'}.`,
      );
    } catch (deleteError) {
      setLocalUsersError(
        deleteError instanceof Error
          ? deleteError.message
          : 'Failed to delete cloned support users.',
      );
    } finally {
      setIsDeletingAllSupportClones(false);
    }
  };

  const handleCloneAllFullyOnboarded = async () => {
    if (Platform.OS === 'web') {
      const confirmed = globalThis.confirm(
        'Clone every active, entitled, fully onboarded non-demo household from the source database?',
      );
      if (!confirmed) return;
    }

    setIsCloningFullyOnboarded(true);
    setLocalUsersError(null);
    setCloneMessage(null);

    try {
      const response = await fetch(getFullyOnboardedSupportClonesEndpoint(), {
        method: 'POST',
      });
      const payload = (await response.json()) as {
        job?: SupportCloneBulkJob;
        error?: string;
      };

      if (!response.ok) {
        throw new Error(payload.error ?? 'Failed to clone fully onboarded households.');
      }

      let job = payload.job;
      if (!job) {
        throw new Error('Clone job did not return a status payload.');
      }

      setCloneMessage(formatSupportCloneBulkJobMessage(job));

      while (job.status === 'running') {
        await wait(1500);

        const statusResponse = await fetch(
          getFullyOnboardedSupportCloneJobEndpoint(job.id),
        );
        const statusPayload = (await statusResponse.json()) as {
          job?: SupportCloneBulkJob;
          error?: string;
        };

        if (!statusResponse.ok) {
          throw new Error(statusPayload.error ?? 'Failed to load clone job status.');
        }

        if (!statusPayload.job) {
          throw new Error('Clone job status did not return a payload.');
        }

        job = statusPayload.job;
        setCloneMessage(formatSupportCloneBulkJobMessage(job));
      }

      await refreshLocalUsers();
      setActiveLocalUserGroup('support');

      if (job.status === 'failed') {
        throw new Error(job.error ?? 'Failed to clone fully onboarded households.');
      }

      setCloneMessage(formatSupportCloneBulkJobMessage(job));
    } catch (cloneError) {
      setLocalUsersError(
        cloneError instanceof Error
          ? cloneError.message
          : 'Failed to clone fully onboarded households.',
      );
    } finally {
      setIsCloningFullyOnboarded(false);
    }
  };

  const handleEmailAuth = async () => {
    trackMarketingCtaClicked({
      route: pathname,
      pageType: 'auth',
      pageSlug: 'login',
      ctaId: mode === 'signin' ? 'email-sign-in' : 'email-create-account',
      destination: mode === 'signin' ? 'sign-in' : 'create-account',
      placement: 'auth-form',
    });

    setError(null);
    setSuccess(null);
    setCaptchaError(null);
    clearPasswordResetPending();

    if (!email || !password) {
      trackAuthAttemptFailed({
        route: pathname,
        intent: mode,
        provider: 'email',
        flow: 'password',
        failureStage: 'validation',
        errorBucket: 'missing_credentials',
      });
      setError('Please enter your email and password.');
      return;
    }

    if (mode === 'signup' && password !== confirmPassword) {
      trackAuthAttemptFailed({
        route: pathname,
        intent: mode,
        provider: 'email',
        flow: 'password',
        failureStage: 'validation',
        errorBucket: 'password_mismatch',
      });
      setError('Passwords do not match.');
      return;
    }

    if (mode === 'signup' && (!acceptedLegalTerms || !acceptedFinancialAck)) {
      trackAuthAttemptFailed({
        route: pathname,
        intent: mode,
        provider: 'email',
        flow: 'password',
        failureStage: 'validation',
        errorBucket: 'legal_acceptance_missing',
      });
      setError('Please accept the terms and financial product acknowledgment to create an account.');
      return;
    }

    if (mode === 'signup') {
      const passwordPolicyError = getPasswordPolicyError(password);
      if (passwordPolicyError) {
        trackAuthAttemptFailed({
          route: pathname,
          intent: mode,
          provider: 'email',
          flow: 'password',
          failureStage: 'validation',
          errorBucket: 'password_policy',
          errorMessage: passwordPolicyError,
        });
        setError(passwordPolicyError);
        return;
      }
    }

    const resolvedCaptchaToken = getCaptchaTokenForAuth('email', 'password');
    if (resolvedCaptchaToken === undefined) {
      return;
    }

    if (mode === 'signup') {
      await markPendingLegalSignupAcceptance();
    }

    setIsLoading(true);
    try {
      await persistRequestedAuthRedirect();

      const pendingAttempt = {
        intent: mode,
        provider: 'email' as const,
        flow: 'password' as const,
        route: pathname,
        startedAt: new Date().toISOString(),
      };
      await setPendingAuthAttempt(pendingAttempt);
      trackAuthAttemptStarted({
        route: pathname,
        intent: mode,
        provider: 'email',
        flow: 'password',
      });

      if (mode === 'signin') {
        const { error: authError } = await supabase.auth.signInWithPassword({
          email,
          password,
          ...(resolvedCaptchaToken
            ? { options: { captchaToken: resolvedCaptchaToken } }
            : {}),
        });
        if (authError) {
          const serializedError = serializeClientError(authError);
          await clearPendingLegalSignupAcceptance();

          trackAuthAttemptFailed({
            route: pathname,
            intent: mode,
            provider: 'email',
            flow: 'password',
            failureStage: 'provider',
            errorBucket: getAuthErrorBucket(authError),
            errorMessage: authError.message,
          });
          await clearPendingAuthAttempt();
          setError(authError.message);
        } else {
          trackAuthAttemptSucceeded({
            route: pathname,
            intent: mode,
            provider: 'email',
            flow: 'password',
          });
          await clearPendingAuthAttempt();
          await redirectAfterSuccessfulAuth();
        }
      } else {
        const { data: signupData, error: authError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: oauthCallbackRedirect,
            ...(resolvedCaptchaToken ? { captchaToken: resolvedCaptchaToken } : {}),
          },
        });
        if (authError) {
          const serializedError = serializeClientError(authError);
          const expectedProviderError = isExpectedEmailSignupProviderError(authError);

          trackAuthAttemptFailed({
            route: pathname,
            intent: mode,
            provider: 'email',
            flow: 'password',
            failureStage: 'provider',
            errorBucket: getAuthErrorBucket(authError),
            errorMessage: authError.message,
          });
          if (!expectedProviderError) {
            reportUiError({
              error: authError,
              message: 'Email signup failed',
              route: pathname,
              screen: 'auth',
              component: 'LoginScreen',
              metadata: {
                intent: mode,
                provider: 'email',
                flow: 'password',
                platform: Platform.OS,
              },
            });
          }
          void sendClientObservability({
            kind: expectedProviderError ? 'event' : 'error',
            eventName: 'auth.email_signup_failed',
            route: pathname,
            screen: 'auth',
            component: 'LoginScreen',
            message: authError.message,
            metadata: {
              intent: mode,
              provider: 'email',
              flow: 'password',
              platform: Platform.OS,
              expectedProviderError,
            },
            ...serializedError,
          });
          await clearPendingAuthAttempt();
          setError(authError.message);
        } else {
          const createdNewUser = wasAuthUserCreatedDuringAttempt({
            attemptStartedAt: pendingAttempt.startedAt,
            userCreatedAt: signupData.user?.created_at ?? null,
          });
          if (createdNewUser) {
            trackAuthUserCreated({
              route: pathname,
              attemptedIntent: mode,
              provider: 'email',
              flow: 'password',
              supabaseUserId: signupData.user?.id ?? null,
              metadata: {
                requiresConfirmation: true,
              },
            });
          }
          trackAuthAttemptSucceeded({
            route: pathname,
            intent: mode,
            provider: 'email',
            flow: 'password',
            metadata: {
              createdNewUser,
              requiresConfirmation: true,
            },
          });
          await clearPendingAuthAttempt();
          await markAuthEntrySeen();
          setSuccess('Account created. Check your inbox for a confirmation link.');
        }
      }
    } finally {
      setIsLoading(false);
      resetCaptcha();
    }
  };

  const handlePasswordResetRequest = async () => {
    setError(null);
    setSuccess(null);
    setCaptchaError(null);

    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    const resolvedCaptchaToken = getCaptchaTokenForAuth('email', 'password');
    if (resolvedCaptchaToken === undefined) {
      return;
    }

    setIsLoading(true);
    try {
      markPasswordResetPending(email);
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: PASSWORD_RESET_REDIRECT,
        ...(resolvedCaptchaToken ? { captchaToken: resolvedCaptchaToken } : {}),
      });

      if (resetError) {
        setError(resetError.message);
      } else {
        setSuccess('Check your inbox for a password reset link.');
      }
    } finally {
      setIsLoading(false);
      resetCaptcha();
    }
  };

  const completeNativeOAuth = async (callbackUrl: string) => {
    const normalizedUrl = callbackUrl.includes('#') ? callbackUrl.replace('#', '?') : callbackUrl;
    const { queryParams } = Linking.parse(normalizedUrl);

    const code = typeof queryParams?.code === 'string' ? queryParams.code : null;
    const accessToken =
      typeof queryParams?.access_token === 'string' ? queryParams.access_token : null;
    const refreshToken =
      typeof queryParams?.refresh_token === 'string' ? queryParams.refresh_token : null;

    if (code) {
      const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
      if (exchangeError) throw exchangeError;
      const { data } = await supabase.auth.getSession();
      return data.session?.user ?? null;
    }

    if (accessToken && refreshToken) {
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      if (sessionError) throw sessionError;
      const { data } = await supabase.auth.getSession();
      return data.session?.user ?? null;
    }

    throw new Error('OAuth callback did not include a session.');
  };

  const handleOAuthSignIn = async (provider: 'google' | 'apple') => {
    setError(null);
    setSuccess(null);
    setCaptchaError(null);
    clearPasswordResetPending();

    if (mode === 'signup' && (!acceptedLegalTerms || !acceptedFinancialAck)) {
      trackAuthAttemptFailed({
        route: pathname,
        intent: mode,
        provider,
        flow: Platform.OS === 'web' ? 'oauth_web' : 'oauth_native',
        failureStage: 'validation',
        errorBucket: 'legal_acceptance_missing',
      });
      setError('Please accept the terms and financial product acknowledgment to create an account.');
      return;
    }

    const flow = Platform.OS === 'web' ? 'oauth_web' : 'oauth_native';
    const resolvedCaptchaToken = getCaptchaTokenForAuth(provider, flow);
    if (resolvedCaptchaToken === undefined) {
      return;
    }

    if (mode === 'signup') {
      await markPendingLegalSignupAcceptance();
    }

    await persistRequestedAuthRedirect();
    const pendingAttempt = {
      intent: mode,
      provider,
      flow,
      route: pathname,
      startedAt: new Date().toISOString(),
    } as const;
    await setPendingAuthAttempt(pendingAttempt);
    trackAuthAttemptStarted({
      route: pathname,
      intent: mode,
      provider,
      flow,
    });

    if (__DEV__ && Platform.OS !== 'web') {
      console.log('[auth] Native OAuth redirect URI:', oauthCallbackRedirect);
    }

    const { data, error: authError } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: oauthCallbackRedirect,
        skipBrowserRedirect: Platform.OS !== 'web',
        ...(resolvedCaptchaToken ? { captchaToken: resolvedCaptchaToken } : {}),
      },
    });

    if (authError) {
      await clearPendingLegalSignupAcceptance();
      trackAuthAttemptFailed({
        route: pathname,
        intent: mode,
        provider,
        flow,
        failureStage: 'provider_start',
        errorBucket: getAuthErrorBucket(authError),
        errorMessage: authError.message,
      });
      await clearPendingAuthAttempt();
      setError(authError.message);
      resetCaptcha();
      return;
    }

    if (Platform.OS === 'web') {
      return;
    }

    if (!data?.url) {
      await clearPendingLegalSignupAcceptance();
      trackAuthAttemptFailed({
        route: pathname,
        intent: mode,
        provider,
        flow,
        failureStage: 'provider_start',
        errorBucket: 'missing_oauth_url',
      });
      await clearPendingAuthAttempt();
      setError('Unable to start sign-in.');
      resetCaptcha();
      return;
    }

    const result = await openOAuthSessionAsync(data.url, oauthCallbackRedirect);

    if (result.type === 'cancel') {
      await clearPendingLegalSignupAcceptance();
      trackAuthAttemptFailed({
        route: pathname,
        intent: mode,
        provider,
        flow,
        failureStage: 'provider',
        errorBucket: 'cancelled',
      });
      await clearPendingAuthAttempt();
      setError('Sign-in was canceled.');
      return;
    }

    if (result.type === 'success') {
      if (!result.url) {
        await clearPendingLegalSignupAcceptance();
        trackAuthAttemptFailed({
          route: pathname,
          intent: mode,
          provider,
          flow,
          failureStage: 'callback',
          errorBucket: 'missing_callback_url',
        });
        await clearPendingAuthAttempt();
        setError('Sign-in did not return a callback URL.');
        return;
      }

      try {
        const authUser = await completeNativeOAuth(result.url);
        const createdNewUser = wasAuthUserCreatedDuringAttempt({
          attemptStartedAt: pendingAttempt.startedAt,
          userCreatedAt: authUser?.created_at ?? null,
        });
        if (createdNewUser) {
          trackAuthUserCreated({
            route: pathname,
            attemptedIntent: pendingAttempt.intent,
            provider,
            flow,
            supabaseUserId: authUser?.id ?? null,
          });
        }
        trackAuthAttemptSucceeded({
          route: pathname,
          intent: mode,
          provider,
          flow,
          metadata: {
            attemptedIntent: pendingAttempt.intent,
            createdNewUser,
          },
        });
        await clearPendingAuthAttempt();
        await redirectAfterSuccessfulAuth();
      } catch (oauthError) {
        await clearPendingLegalSignupAcceptance();
        trackAuthAttemptFailed({
          route: pathname,
          intent: mode,
          provider,
          flow,
          failureStage: 'session_exchange',
          errorBucket: getAuthErrorBucket(oauthError),
          errorMessage: oauthError instanceof Error ? oauthError.message : null,
        });
        await clearPendingAuthAttempt();
        setError(oauthError instanceof Error ? oauthError.message : 'Failed to complete sign-in.');
        resetCaptcha();
      }
      return;
    }

    await clearPendingLegalSignupAcceptance();
    trackAuthAttemptFailed({
      route: pathname,
      intent: mode,
      provider,
      flow,
      failureStage: 'provider',
      errorBucket: result.type,
    });
    await clearPendingAuthAttempt();
    setError(`Sign-in did not complete in the app (result: ${result.type}).`);
    resetCaptcha();
  };

  const handleGoogleSignIn = async () => {
    trackMarketingCtaClicked({
      route: pathname,
      pageType: 'auth',
      pageSlug: 'login',
      ctaId: 'continue-with-google',
      destination: mode === 'signin' ? 'sign-in' : 'create-account',
      placement: 'oauth',
    });

    setError(null);
    setSuccess(null);
    setCaptchaError(null);

    if (mode === 'signup' && (!acceptedLegalTerms || !acceptedFinancialAck)) {
      trackAuthAttemptFailed({
        route: pathname,
        intent: mode,
        provider: 'google',
        flow: 'id_token_native',
        failureStage: 'validation',
        errorBucket: 'legal_acceptance_missing',
      });
      setError('Please accept the terms and financial product acknowledgment to create an account.');
      return;
    }

    if (Platform.OS !== 'android') {
      // The public RN Google Sign-In package does not let us provide the raw
      // nonce that Supabase expects when the returned iOS ID token contains a
      // nonce claim. Route non-Android clients through the OAuth flow instead.
      await handleOAuthSignIn('google');
      return;
    }

    if (!canUseNativeGoogleSignIn()) {
      trackAuthAttemptFailed({
        route: pathname,
        intent: mode,
        provider: 'google',
        flow: 'id_token_native',
        failureStage: 'provider_start',
        errorBucket: 'google_native_not_configured',
      });
      setError('Native Google Sign-In is not configured.');
      return;
    }

    const resolvedCaptchaToken = getCaptchaTokenForAuth('google', 'id_token_native');
    if (resolvedCaptchaToken === undefined) {
      return;
    }

    setIsLoading(true);

    try {
      if (mode === 'signup') {
        await markPendingLegalSignupAcceptance();
      }

      const pendingAttempt = {
        intent: mode,
        provider: 'google',
        flow: 'id_token_native',
        route: pathname,
        startedAt: new Date().toISOString(),
      } as const;
      await setPendingAuthAttempt(pendingAttempt);
      trackAuthAttemptStarted({
        route: pathname,
        intent: mode,
        provider: 'google',
        flow: 'id_token_native',
      });

      if (__DEV__) {
        console.log('[auth][google] starting native Google sign-in');
      }

      const result = await signInWithNativeGoogle();

      if (result.cancelled) {
        await clearPendingLegalSignupAcceptance();
        trackAuthAttemptFailed({
          route: pathname,
          intent: mode,
          provider: 'google',
          flow: 'id_token_native',
          failureStage: 'provider',
          errorBucket: 'cancelled',
        });
        await clearPendingAuthAttempt();
        setError('Sign-in was canceled.');
        return;
      }

      const authData = await signInWithProviderIdToken({
        provider: 'google',
        token: result.idToken,
        accessToken: result.accessToken,
        captchaToken: resolvedCaptchaToken,
      });

      if (__DEV__) {
        console.log('[auth][google] signInWithIdToken finished', {
          hasSession: !!authData.session,
          userId: authData.user?.id ?? null,
          userEmail: authData.user?.email ?? null,
        });
      }

      await saveAuthProfileMetadata({
        email: result.profile.email,
        full_name: result.profile.fullName,
        given_name: result.profile.givenName,
        family_name: result.profile.familyName,
        avatar_url: result.profile.photo,
      });
      const createdNewUser = wasAuthUserCreatedDuringAttempt({
        attemptStartedAt: pendingAttempt.startedAt,
        userCreatedAt: authData.user?.created_at ?? null,
      });
      if (createdNewUser) {
        trackAuthUserCreated({
          route: pathname,
          attemptedIntent: pendingAttempt.intent,
          provider: 'google',
          flow: 'id_token_native',
          supabaseUserId: authData.user?.id ?? null,
        });
      }
      trackAuthAttemptSucceeded({
        route: pathname,
        intent: mode,
        provider: 'google',
        flow: 'id_token_native',
        metadata: {
          attemptedIntent: pendingAttempt.intent,
          createdNewUser,
        },
      });
      await clearPendingAuthAttempt();
      await redirectAfterSuccessfulAuth();
    } catch (googleError) {
      await clearPendingLegalSignupAcceptance();
      if (__DEV__) {
        console.log('[auth][google] sign-in failed', googleError);
      }

      trackAuthAttemptFailed({
        route: pathname,
        intent: mode,
        provider: 'google',
        flow: 'id_token_native',
        failureStage: 'provider',
        errorBucket: getAuthErrorBucket(googleError),
        errorMessage:
          googleError instanceof Error
            ? googleError.message
            : getNativeGoogleSignInErrorMessage(googleError),
      });
      await clearPendingAuthAttempt();
      setError(getNativeGoogleSignInErrorMessage(googleError));
      resetCaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    trackMarketingCtaClicked({
      route: pathname,
      pageType: 'auth',
      pageSlug: 'login',
      ctaId: 'continue-with-apple',
      destination: mode === 'signin' ? 'sign-in' : 'create-account',
      placement: 'oauth',
    });

    setError(null);
    setSuccess(null);
    setCaptchaError(null);

    if (mode === 'signup' && (!acceptedLegalTerms || !acceptedFinancialAck)) {
      trackAuthAttemptFailed({
        route: pathname,
        intent: mode,
        provider: 'apple',
        flow: 'id_token_native',
        failureStage: 'validation',
        errorBucket: 'legal_acceptance_missing',
      });
      setError('Please accept the terms and financial product acknowledgment to create an account.');
      return;
    }

    if (Platform.OS !== 'ios') {
      await handleOAuthSignIn('apple');
      return;
    }

    if (!isAppleAvailable) {
      trackAuthAttemptFailed({
        route: pathname,
        intent: mode,
        provider: 'apple',
        flow: 'id_token_native',
        failureStage: 'provider_start',
        errorBucket: 'apple_not_available',
      });
      setError('Sign in with Apple is not available on this device.');
      return;
    }

    const resolvedCaptchaToken = getCaptchaTokenForAuth('apple', 'id_token_native');
    if (resolvedCaptchaToken === undefined) {
      return;
    }

    setIsLoading(true);
    try {
      if (mode === 'signup') {
        await markPendingLegalSignupAcceptance();
      }

      const pendingAttempt = {
        intent: mode,
        provider: 'apple',
        flow: 'id_token_native',
        route: pathname,
        startedAt: new Date().toISOString(),
      } as const;
      await setPendingAuthAttempt(pendingAttempt);
      trackAuthAttemptStarted({
        route: pathname,
        intent: mode,
        provider: 'apple',
        flow: 'id_token_native',
      });

      if (__DEV__) {
        console.log('[auth][apple] starting native Apple sign-in');
      }

      const rawNonce = createNonce();
      const hashedNonce = await Crypto.digestStringAsync(
        Crypto.CryptoDigestAlgorithm.SHA256,
        rawNonce
      );
      const credential = await AppleAuthentication.signInAsync({
        requestedScopes: [
          AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
          AppleAuthentication.AppleAuthenticationScope.EMAIL,
        ],
        nonce: hashedNonce,
      });

      if (__DEV__) {
        console.log('[auth][apple] credential received', {
          user: credential.user,
          hasIdentityToken: !!credential.identityToken,
          hasAuthorizationCode: !!credential.authorizationCode,
          email: credential.email,
          fullName: credential.fullName,
        });
      }

      if (!credential.identityToken) {
        throw new Error('Apple did not return an identity token.');
      }

      const authData = await signInWithProviderIdToken({
        provider: 'apple',
        token: credential.identityToken,
        captchaToken: resolvedCaptchaToken,
        nonce: rawNonce,
      });

      if (__DEV__) {
        console.log('[auth][apple] signInWithIdToken finished', {
          hasSession: !!authData.session,
          userId: authData.user?.id ?? null,
          userEmail: authData.user?.email ?? null,
          identities:
            authData.user?.identities?.map((identity) => ({
              id: identity.identity_id,
              provider: identity.provider,
            })) ?? [],
          error: null,
        });
      }

      const fullName = getFullName(credential.fullName);
      await saveAuthProfileMetadata({
        email: credential.email,
        full_name: fullName,
        given_name: credential.fullName?.givenName,
        family_name: credential.fullName?.familyName,
      });
      const createdNewUser = wasAuthUserCreatedDuringAttempt({
        attemptStartedAt: pendingAttempt.startedAt,
        userCreatedAt: authData.user?.created_at ?? null,
      });
      if (createdNewUser) {
        trackAuthUserCreated({
          route: pathname,
          attemptedIntent: pendingAttempt.intent,
          provider: 'apple',
          flow: 'id_token_native',
          supabaseUserId: authData.user?.id ?? null,
        });
      }
      trackAuthAttemptSucceeded({
        route: pathname,
        intent: mode,
        provider: 'apple',
        flow: 'id_token_native',
        metadata: {
          attemptedIntent: pendingAttempt.intent,
          createdNewUser,
        },
      });
      await clearPendingAuthAttempt();
      await redirectAfterSuccessfulAuth();

      if (__DEV__) {
        const { data: sessionData } = await supabase.auth.getSession();
        const { data: identitiesData, error: identitiesError } = await supabase.auth.getUserIdentities();
        console.log('[auth][apple] post-login session snapshot', {
          sessionUserId: sessionData.session?.user.id ?? null,
          sessionEmail: sessionData.session?.user.email ?? null,
          identityProviders:
            identitiesData?.identities?.map((identity) => identity.provider) ?? [],
          identitiesError: identitiesError?.message ?? null,
        });
      }
    } catch (appleError) {
      await clearPendingLegalSignupAcceptance();
      if (__DEV__) {
        console.log('[auth][apple] sign-in failed', appleError);
      }

      if (
        typeof appleError === 'object' &&
        appleError !== null &&
        'code' in appleError &&
        appleError.code === 'ERR_REQUEST_CANCELED'
      ) {
        trackAuthAttemptFailed({
          route: pathname,
          intent: mode,
          provider: 'apple',
          flow: 'id_token_native',
          failureStage: 'provider',
          errorBucket: 'cancelled',
        });
        await clearPendingAuthAttempt();
        setError('Sign-in was canceled.');
      } else {
        trackAuthAttemptFailed({
          route: pathname,
          intent: mode,
          provider: 'apple',
          flow: 'id_token_native',
          failureStage: 'provider',
          errorBucket: getAuthErrorBucket(appleError),
          errorMessage:
            appleError instanceof Error
              ? appleError.message
              : 'Failed to complete sign-in.',
        });
        await clearPendingAuthAttempt();
        setError(appleError instanceof Error ? appleError.message : 'Failed to complete sign-in.');
      }
      resetCaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  const testLocalUsers = localUsers.filter((user) => !user.isSupportClone);
  const supportLocalUsers = localUsers
    .filter((user) => user.isSupportClone)
    .sort((a, b) => {
      const householdCreatedAtDelta =
        getTimestampMs(b.householdCreatedAt) - getTimestampMs(a.householdCreatedAt);
      if (householdCreatedAtDelta !== 0) return householdCreatedAtDelta;
      return getTimestampMs(b.createdAt) - getTimestampMs(a.createdAt);
    });
  const visibleLocalUsers =
    activeLocalUserGroup === 'support' ? supportLocalUsers : testLocalUsers;
  const isSupportCloneActionBusy =
    isCloningHousehold ||
    isCloningFullyOnboarded ||
    isDeletingAllSupportClones ||
    deletingUserId !== null ||
    impersonatingUserId !== null;
  const cloneSelectorPlaceholder =
    cloneSelectorType === 'email'
      ? 'source@example.com'
      : cloneSelectorType === 'supabaseUserId'
        ? 'Supabase user UUID'
        : 'Household UUID';
  const demoPickerModal = SHOW_NATIVE_DEMO_CTA ? (
    <DemoProfilePickerModal
      visible={showDemoPicker}
      onClose={() => setShowDemoPicker(false)}
      accounts={NATIVE_DEMO_ACCOUNTS}
      launchingDemoKey={launchingDemoKey}
      onLaunchDemo={handleLaunchDemo}
    />
  ) : null;

  if (showLocalUserPicker) {
    return (
      <ScrollView className="flex-1 bg-gray-950">
        <WebHead><title>Local Users – Per Diem</title></WebHead>
        <View className="min-h-full items-center px-6 py-12">
          <View className="w-full max-w-6xl">
            <Pressable onPress={handleAuthBrandPress}>
              <Text className="mb-2 text-4xl font-bold text-white">Per Diem</Text>
            </Pressable>
            <Text className="text-base text-gray-300">
              Pick a local user from the DB to sign in instantly on localhost.
            </Text>

            <View className="mt-4 flex-row items-center justify-between">
              <Text className="text-sm text-gray-500">
                {localUsers.length} local user{localUsers.length === 1 ? '' : 's'}
              </Text>
              <Pressable onPress={() => router.replace(buildLoginRoute(true))}>
                <Text className="text-sm font-medium text-emerald-300">
                  Use regular login
                </Text>
              </Pressable>
            </View>

            <View className="mt-6 flex-row flex-wrap" style={{ gap: 12 }}>
              {[
                { key: 'test' as const, label: 'Test users', count: testLocalUsers.length },
                { key: 'support' as const, label: 'Cloned support users', count: supportLocalUsers.length },
              ].map((group) => {
                const isActive = activeLocalUserGroup === group.key;
                return (
                  <Pressable
                    key={group.key}
                    onPress={() => setActiveLocalUserGroup(group.key)}
                    className={`rounded-2xl border px-4 py-3 ${
                      isActive
                        ? 'border-emerald-400 bg-emerald-400/15'
                        : 'border-white/10 bg-white/5'
                    }`}
                  >
                    <Text
                      className={`text-sm font-semibold ${
                        isActive ? 'text-emerald-100' : 'text-gray-300'
                      }`}
                    >
                      {group.label}
                    </Text>
                    <Text className="mt-1 text-xs text-gray-500">
                      {group.count} user{group.count === 1 ? '' : 's'}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            {activeLocalUserGroup === 'support' ? (
              <View className="mt-6 rounded-3xl border border-emerald-500/20 bg-emerald-950/20 p-5">
                <View className="flex-row flex-wrap items-end" style={{ gap: 12 }}>
                  <View className="min-w-[400px]">
                    <Text className="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-200">
                      Clone household
                    </Text>
                    <View className="flex-row rounded-2xl bg-black/20 p-1" style={{ gap: 4 }}>
                      {[
                        { key: 'email' as const, label: 'Email', width: 100 },
                        { key: 'supabaseUserId' as const, label: 'User ID', width: 120 },
                        { key: 'householdId' as const, label: 'Household ID', width: 168 },
                      ].map((selector) => {
                        const isActive = cloneSelectorType === selector.key;
                        return (
                          <Pressable
                            key={selector.key}
                            onPress={() => setCloneSelectorType(selector.key)}
                            className={`rounded-xl px-3 py-2 ${
                              isActive ? 'bg-emerald-400' : 'bg-transparent'
                            }`}
                            style={{ width: selector.width }}
                          >
                          <Text
                            numberOfLines={1}
                            className={`text-center text-xs font-semibold ${
                              isActive ? 'text-gray-950' : 'text-gray-300'
                            }`}
                          >
                              {selector.label}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </View>
                  </View>

                  <View className="min-w-[260px] flex-[2]">
                    <Text className="mb-2 text-xs font-medium text-gray-400">
                      Source selector
                    </Text>
                    <TextInput
                      className="rounded-2xl border border-white/10 bg-gray-900 px-4 py-3 text-sm text-white"
                      placeholder={cloneSelectorPlaceholder}
                      placeholderTextColor="#6b7280"
                      value={cloneSelectorValue}
                      onChangeText={(value) => {
                        setCloneSelectorValue(value);
                        setLocalUsersError(null);
                        setCloneMessage(null);
                      }}
                      autoCapitalize="none"
                      autoCorrect={false}
                    />
                  </View>

                  <View className="min-w-[220px] flex-1">
                    <Text className="mb-2 text-xs font-medium text-gray-400">
                      Local email override
                    </Text>
                    <TextInput
                      className="rounded-2xl border border-white/10 bg-gray-900 px-4 py-3 text-sm text-white"
                      placeholder="optional@perdiem.local"
                      placeholderTextColor="#6b7280"
                      value={cloneLocalEmail}
                      onChangeText={(value) => {
                        setCloneLocalEmail(value);
                        setLocalUsersError(null);
                        setCloneMessage(null);
                      }}
                      autoCapitalize="none"
                      autoCorrect={false}
                      keyboardType="email-address"
                    />
                  </View>

                  <Pressable
                    onPress={handleCloneHousehold}
                    disabled={isSupportCloneActionBusy || !cloneSelectorValue.trim()}
                    className={`min-w-[160px] rounded-2xl px-5 py-3 ${
                      isSupportCloneActionBusy || !cloneSelectorValue.trim()
                        ? 'bg-gray-700'
                        : 'bg-emerald-400'
                    }`}
                  >
                    {isCloningHousehold ? (
                      <View className="flex-row items-center justify-center">
                        <ActivityIndicator color="#ffffff" />
                        <Text className="ml-2 text-sm font-semibold text-white">
                          Cloning
                        </Text>
                      </View>
                    ) : (
                      <Text className="text-center text-sm font-semibold text-gray-950">
                        Clone household
                      </Text>
                    )}
                  </Pressable>
                </View>

                <View
                  className="mt-5 flex-row flex-wrap items-center border-t border-white/10 pt-5"
                  style={{ gap: 12 }}
                >
                  <Text className="mr-2 text-xs font-semibold uppercase tracking-wide text-emerald-200">
                    Bulk actions
                  </Text>

                  <Pressable
                    onPress={handleCloneAllFullyOnboarded}
                    disabled={isSupportCloneActionBusy}
                    className={`min-w-[220px] rounded-2xl px-5 py-3 ${
                      isSupportCloneActionBusy ? 'bg-gray-700' : 'bg-sky-400'
                    }`}
                  >
                    {isCloningFullyOnboarded ? (
                      <View className="flex-row items-center justify-center">
                        <ActivityIndicator color="#ffffff" />
                        <Text className="ml-2 text-sm font-semibold text-white">
                          Cloning all
                        </Text>
                      </View>
                    ) : (
                      <Text className="text-center text-sm font-semibold text-gray-950">
                        Clone all fully onboarded
                      </Text>
                    )}
                  </Pressable>

                  <Pressable
                    onPress={handleDeleteAllSupportClones}
                    disabled={isSupportCloneActionBusy || supportLocalUsers.length === 0}
                    className={`min-w-[140px] rounded-2xl px-5 py-3 ${
                      isSupportCloneActionBusy || supportLocalUsers.length === 0
                        ? 'bg-gray-700'
                        : 'bg-red-500'
                    }`}
                  >
                    {isDeletingAllSupportClones ? (
                      <View className="flex-row items-center justify-center">
                        <ActivityIndicator color="#ffffff" />
                        <Text className="ml-2 text-sm font-semibold text-white">
                          Deleting
                        </Text>
                      </View>
                    ) : (
                      <Text className="text-center text-sm font-semibold text-white">
                        Delete all
                      </Text>
                    )}
                  </Pressable>
                </View>
              </View>
            ) : null}

            {cloneMessage ? (
              <View className="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-950/40 px-4 py-3">
                <Text className="text-sm text-emerald-100">{cloneMessage}</Text>
              </View>
            ) : null}

            {localUsersError ? (
              <View className="mt-6 rounded-2xl border border-red-500/40 bg-red-950/40 px-4 py-3">
                <Text className="text-sm text-red-200">{localUsersError}</Text>
              </View>
            ) : null}

            {isLoadingLocalUsers ? (
              <View className="mt-12 items-center">
                <ActivityIndicator color="#34d399" />
                <Text className="mt-3 text-sm text-gray-400">Loading local users...</Text>
              </View>
            ) : (
              <View className="mt-8 flex-row flex-wrap" style={{ gap: 16 }}>
                {visibleLocalUsers.map((user) => {
                  const isSigningIn = impersonatingUserId === user.supabaseUserId;
                  const isDeleting = deletingUserId === user.supabaseUserId;
                  const displayName =
                    user.displayName && user.displayName !== user.email
                      ? user.displayName
                      : 'Unnamed local user';
                  const shouldShowEmail = !user.isSupportClone && user.email;
                  const sourceHouseholdLabel =
                    user.isSupportClone && user.sourceHouseholdId
                      ? `Source household ${user.sourceHouseholdId}`
                      : null;
                  const householdLabel = getLocalDevOnboardingLabel(user);
                  const householdCreatedLabel = user.householdId
                    ? `Household created ${formatLocalDevDate(user.householdCreatedAt)}`
                    : null;
                  const fiAgeLabel =
                    user.fiAge != null && Number.isFinite(user.fiAge)
                      ? `FI age ${user.fiAge}`
                      : null;
                  const netWorthLabel =
                    user.netWorth != null && Number.isFinite(user.netWorth)
                      ? `Net worth ${formatCurrency(user.netWorth, { compact: true, decimals: 0 })}`
                      : null;
                  const enrollmentLabel = formatLocalDevEnrollmentStatus(user);
                  const roleLabel = user.personType === 'spouse' ? 'Spouse' : 'Primary';

                  return (
                    <View
                      key={user.supabaseUserId}
                      className="rounded-3xl border border-white/10 bg-white/5 p-5"
                      style={{ flexBasis: 280, flexGrow: 1, minWidth: 260 }}
                    >
                      <View className="flex-row items-start justify-between">
                        <View className="mr-4 flex-1">
                          <Text className="text-lg font-semibold text-white">
                            {displayName}
                          </Text>
                          {shouldShowEmail ? (
                            <Text className="mt-1 text-sm text-gray-400">
                              {user.email}
                            </Text>
                          ) : null}
                          {sourceHouseholdLabel ? (
                            <Text className="mt-1 text-xs text-gray-500">
                              {sourceHouseholdLabel}
                            </Text>
                          ) : null}
                        </View>
                        <View className="rounded-full bg-emerald-500/15 px-3 py-1">
                          <Text className="text-xs font-medium uppercase tracking-wide text-emerald-200">
                            {roleLabel}
                          </Text>
                        </View>
                        <Pressable
                          onPress={() => handleDeleteLocalUser(user)}
                          disabled={isSupportCloneActionBusy}
                          className="ml-2 h-8 w-8 items-center justify-center rounded-full bg-red-500/15"
                        >
                          <Text className="text-base font-semibold text-red-200">
                            {isDeleting ? '...' : 'X'}
                          </Text>
                        </Pressable>
                      </View>

                      <View className="mt-6" style={{ gap: 8 }}>
                        <Text className="text-sm text-gray-300">{householdLabel}</Text>
                        {householdCreatedLabel ? (
                          <Text className="text-sm text-gray-400">
                            {householdCreatedLabel}
                          </Text>
                        ) : null}
                        {fiAgeLabel || netWorthLabel ? (
                          <Text className="text-sm text-gray-400">
                            {[fiAgeLabel, netWorthLabel].filter(Boolean).join(' | ')}
                          </Text>
                        ) : null}
                        <Text className="text-sm text-gray-500">
                          {enrollmentLabel}
                        </Text>
                      </View>

                      <Pressable
                        onPress={() => handleLocalUserLogin(user)}
                        disabled={isSupportCloneActionBusy}
                        className="mt-6 rounded-2xl bg-white/5 px-4 py-3"
                      >
                        {isSigningIn ? (
                          <View className="flex-row items-center justify-center">
                            <ActivityIndicator color="#ffffff" />
                            <Text className="ml-3 text-sm font-medium text-white">
                              Signing in...
                            </Text>
                          </View>
                        ) : (
                          <Text className="text-center text-sm font-semibold text-white">
                            Continue as this user
                          </Text>
                        )}
                      </Pressable>
                    </View>
                  );
                })}

                {!isLoadingLocalUsers && visibleLocalUsers.length === 0 ? (
                  <View className="w-full rounded-3xl border border-white/10 bg-white/5 px-6 py-8">
                    <Text className="text-base font-medium text-white">
                      No {activeLocalUserGroup === 'support' ? 'cloned support users' : 'test users'} found.
                    </Text>
                    <Text className="mt-2 text-sm text-gray-400">
                      {activeLocalUserGroup === 'support'
                        ? 'Clone a household from the form above to add one here.'
                        : 'Create or seed a local auth user, then refresh this page.'}
                    </Text>
                  </View>
                ) : null}
              </View>
            )}
          </View>
        </View>
      </ScrollView>
    );
  }

  if (DEV_BYPASS && !isRegularLoginRequested) {
    return (
      <View className="flex-1 items-center justify-center bg-gray-950 px-8">
        <WebHead><title>{mode === 'signup' ? 'Sign Up' : 'Sign In'} – Per Diem</title></WebHead>
        <Pressable onPress={handleAuthBrandPress}>
          <Text className="mb-2 text-4xl font-bold text-white">Per Diem</Text>
        </Pressable>
        <Text className="mb-12 text-base text-gray-400">
          Your path to financial independence
        </Text>
        <Pressable
          onPress={handleDevLogin}
          className="mb-4 w-full rounded-xl bg-yellow-500 px-6 py-4"
        >
          <Text className="text-center text-base font-semibold text-gray-900 dark:text-gray-100">
            Dev Login (bypass)
          </Text>
        </Pressable>
      </View>
    );
  }

  if (isCheckingFreshInstallIntro) {
    return (
      <View className="flex-1 items-center justify-center bg-gray-950 px-8">
        <WebHead><title>Per Diem</title></WebHead>
        <ActivityIndicator color="#34d399" />
      </View>
    );
  }

  if (showFreshInstallIntro) {
    return (
      <>
        <NativeFreshInstallIntro
          onStart={() => handleFreshInstallAuthChoice('signup')}
          onSignIn={() => handleFreshInstallAuthChoice('signin')}
          onTryDemo={handleOpenDemoPicker}
        />
        {demoPickerModal}
      </>
    );
  }

  return (
    <>
      <ScrollView
        className="flex-1 bg-gray-950"
        contentContainerStyle={authFormStyles.scrollContent}
        contentInsetAdjustmentBehavior="automatic"
        keyboardShouldPersistTaps="handled"
      >
        <View className="items-center" style={authFormStyles.form}>
        <WebHead><title>{mode === 'signup' ? 'Sign Up' : 'Sign In'} – Per Diem</title></WebHead>
        <Pressable onPress={handleAuthBrandPress}>
          <Text className="mb-2 text-4xl font-bold text-white">Per Diem</Text>
        </Pressable>
        <Text className="mb-12 text-base text-gray-400">
          Your path to financial independence
        </Text>

        {isLocalhost ? (
          <Pressable onPress={() => router.replace(buildLoginRoute(false))} className="mb-6">
            <Text className="text-center text-sm font-medium text-emerald-300">
              Use local user tiles instead
            </Text>
          </Pressable>
        ) : null}

        {/* Email/Password form */}
        <TextInput
          testID="auth-email-input"
          accessibilityLabel="Email"
          className="mb-3 w-full rounded-xl bg-gray-800 px-4 py-4 text-base text-white"
          placeholder="Email"
          placeholderTextColor="#6b7280"
          value={email}
          onChangeText={(t) => { setEmail(t); setError(null); setSuccess(null); }}
          autoCapitalize="none"
          keyboardType="email-address"
          autoComplete="email"
        />
        {!isResetMode ? (
          <TextInput
            testID="auth-password-input"
            accessibilityLabel="Password"
            className={`${mode === 'signup' ? 'mb-2' : 'mb-3'} w-full rounded-xl bg-gray-800 px-4 py-4 text-base text-white`}
            placeholder="Password"
            placeholderTextColor="#6b7280"
            value={password}
            onChangeText={(t) => { setPassword(t); setError(null); setSuccess(null); }}
            secureTextEntry={!shouldDisablePasswordAutofill}
            autoComplete={shouldDisablePasswordAutofill ? 'off' : 'password'}
            textContentType={shouldDisablePasswordAutofill ? 'none' : 'password'}
            returnKeyType={mode === 'signin' ? 'go' : 'next'}
            onSubmitEditing={mode === 'signin' ? handleEmailAuth : undefined}
          />
        ) : null}

        {mode === 'signup' && !isResetMode ? (
          <PasswordRequirementList password={password} className="mb-3" />
        ) : null}

        {mode === 'signup' && !isResetMode && (
          <TextInput
            testID="auth-confirm-password-input"
            accessibilityLabel="Confirm password"
            className="mb-3 w-full rounded-xl bg-gray-800 px-4 py-4 text-base text-white"
            placeholder="Confirm password"
            placeholderTextColor="#6b7280"
            value={confirmPassword}
            onChangeText={(t) => { setConfirmPassword(t); setError(null); setSuccess(null); }}
            secureTextEntry={!shouldDisablePasswordAutofill}
            autoComplete={shouldDisablePasswordAutofill ? 'off' : 'password'}
            textContentType={shouldDisablePasswordAutofill ? 'none' : 'password'}
            returnKeyType="go"
            onSubmitEditing={handleEmailAuth}
          />
        )}

        {mode === 'signup' && !isResetMode ? (
          <View className="mb-4 w-full gap-3">
            <View className="flex-row gap-3">
              <Pressable
                testID="auth-legal-terms-checkbox"
                accessibilityRole="checkbox"
                accessibilityLabel="Accept legal terms"
                accessibilityState={{ checked: acceptedLegalTerms }}
                onPress={() => {
                  setAcceptedLegalTerms((value) => !value);
                  setError(null);
                }}
                className={`mt-0.5 h-5 w-5 items-center justify-center rounded border ${
                  acceptedLegalTerms ? 'border-forest-500 bg-forest-600' : 'border-gray-600'
                }`}
              >
                {acceptedLegalTerms ? <Ionicons name="checkmark" size={14} color="#ffffff" /> : null}
              </Pressable>
              <View className="flex-1">
                <Pressable
                  onPress={() => {
                    setAcceptedLegalTerms((value) => !value);
                    setError(null);
                  }}
                >
                  <Text className="text-sm leading-5 text-gray-300">
                    I agree to Per Diem's Terms of Service and acknowledge the Privacy Policy.
                  </Text>
                </Pressable>
                <View className="mt-1 flex-row gap-4">
                  <Pressable onPress={() => openExternalUrl(TERMS_OF_SERVICE_URL)}>
                    <Text className="text-xs font-medium text-emerald-300">Terms of Service</Text>
                  </Pressable>
                  <Pressable onPress={() => openExternalUrl(PRIVACY_POLICY_URL)}>
                    <Text className="text-xs font-medium text-emerald-300">Privacy Policy</Text>
                  </Pressable>
                </View>
              </View>
            </View>

            <Pressable
              testID="auth-financial-ack-checkbox"
              accessibilityRole="checkbox"
              accessibilityLabel="Accept financial product acknowledgment"
              accessibilityState={{ checked: acceptedFinancialAck }}
              onPress={() => {
                setAcceptedFinancialAck((value) => !value);
                setError(null);
              }}
              className="flex-row gap-3"
            >
              <View
                className={`mt-0.5 h-5 w-5 items-center justify-center rounded border ${
                  acceptedFinancialAck ? 'border-forest-500 bg-forest-600' : 'border-gray-600'
                }`}
              >
                {acceptedFinancialAck ? <Ionicons name="checkmark" size={14} color="#ffffff" /> : null}
              </View>
              <View className="flex-1">
                <Text className="text-sm leading-5 text-gray-300">
                  {FINANCIAL_ACKNOWLEDGMENT_TEXT}
                </Text>
              </View>
            </Pressable>
          </View>
        ) : null}

        {mode === 'signin' && !isResetMode ? (
          <Pressable
            onPress={() => {
              setIsResetMode(true);
              setError(null);
              setSuccess(null);
              setPassword('');
              setConfirmPassword('');
            }}
            className="mb-4 self-end"
          >
            <Text className="text-sm font-medium text-emerald-300">Forgot password?</Text>
          </Pressable>
        ) : null}

        {/* Inline error message */}
        {error && (
          <View
            testID="auth-error-message"
            className="mb-3 w-full rounded-xl bg-red-900/50 px-4 py-3"
          >
            <Text className="text-sm text-red-300">{error}</Text>
          </View>
        )}

        {/* Inline success message */}
        {success && (
          <View
            testID="auth-success-message"
            className="mb-3 w-full rounded-xl bg-green-900/50 px-4 py-3"
          >
            <Text className="text-sm text-green-300">{success}</Text>
          </View>
        )}

        <TurnstileCaptcha
          siteKey={isCaptchaEnabled ? TURNSTILE_SITE_KEY : ''}
          resetKey={captchaResetKey}
          onTokenChange={setCaptchaToken}
          onErrorChange={setCaptchaError}
        />

        {captchaError ? (
          <View className="mb-3 w-full rounded-xl bg-amber-900/40 px-4 py-3">
            <Text className="text-sm text-amber-200">{captchaError}</Text>
          </View>
        ) : null}

        <Pressable
          testID="auth-submit-button"
          accessibilityRole="button"
          accessibilityLabel={
            isResetMode ? 'Send reset link' : mode === 'signin' ? 'Sign in' : 'Create account'
          }
          onPress={isResetMode ? handlePasswordResetRequest : handleEmailAuth}
          disabled={isLoading}
          className="mb-3 w-full rounded-xl bg-forest-600 px-6 py-4"
        >
          {isLoading ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text className="text-center text-base font-semibold text-white">
              {isResetMode ? 'Send Reset Link' : mode === 'signin' ? 'Sign In' : 'Create Account'}
            </Text>
          )}
        </Pressable>

        <Pressable
          testID="auth-toggle-mode-button"
          accessibilityRole="button"
          accessibilityLabel={
            isResetMode
              ? 'Back to sign in'
              : mode === 'signin'
                ? 'Switch to sign up'
                : 'Switch to sign in'
          }
          onPress={() => {
            if (isResetMode) {
              setIsResetMode(false);
              setMode('signin');
            } else {
              setMode(mode === 'signin' ? 'signup' : 'signin');
            }
            setError(null);
            setSuccess(null);
            setPassword('');
            setConfirmPassword('');
            setAcceptedLegalTerms(false);
            setAcceptedFinancialAck(false);
          }}
          className="mb-8"
        >
          <Text className="text-center text-sm text-gray-400">
            {isResetMode
              ? 'Back to sign in'
              : mode === 'signin'
              ? "Don't have an account? Sign up"
              : 'Already have an account? Sign in'}
          </Text>
        </Pressable>

        {/* OAuth — kept for when Google/Apple are configured */}
        {!isResetMode ? (
          <View className="w-full gap-3">
            <Pressable
              onPress={handleGoogleSignIn}
              disabled={isLoading}
              className="w-full rounded-xl bg-white px-6 py-4"
            >
              <Text className="text-center text-base font-semibold text-gray-900">
                Continue with Google
              </Text>
            </Pressable>

            {Platform.OS === 'ios' && isAppleAvailable ? (
              <AppleAuthentication.AppleAuthenticationButton
                buttonType={AppleAuthentication.AppleAuthenticationButtonType.CONTINUE}
                buttonStyle={AppleAuthentication.AppleAuthenticationButtonStyle.WHITE}
                cornerRadius={12}
                style={{ height: 56, width: '100%' }}
                onPress={handleAppleSignIn}
              />
            ) : Platform.OS !== 'android' ? (
              <Pressable
                onPress={handleAppleSignIn}
                disabled={isLoading}
                className="w-full rounded-xl bg-gray-800 px-6 py-4"
              >
                <Text className="text-center text-base font-semibold text-white">
                  Continue with Apple
                </Text>
              </Pressable>
            ) : null}
          </View>
        ) : null}
        </View>
      </ScrollView>
      {demoPickerModal}
    </>
  );
}
